// novem atom — section title (NovemHR-ekranlar port, Phase 8.6 promotion).
//
// The serif 16px section heading from the novem design (employee-core.jsx
// detail-drawer section heads). DISTINCT from SectionLabel: SectionLabel is
// the 11px uppercase micro-heading that captions a field group; SectionTitle
// is the larger serif editorial heading that opens a section of a detail
// surface. Promoted out of employee-list-content.tsx where it was re-typed
// inline.
//
// Polymorphic `as`: defaults to a non-semantic <div> (the detail-drawer
// sections are visual groupings, not a document heading outline — keeping
// it out of the heading tree avoids polluting screen-reader heading
// navigation, same stance as SectionLabel). Callers that genuinely want it
// in the heading outline pass as="h2" etc.
//
// Accessibility: the host owns the i18n'd text (children). No ARIA is added
// — when as="div" it's plain text; when promoted to a heading the native
// element carries the role.

import { forwardRef, type ElementType, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export interface SectionTitleProps extends HTMLAttributes<HTMLElement> {
  /** The title text (host supplies the i18n key result). */
  readonly children: ReactNode;
  /** Element to render. Defaults to 'div' (decorative, not a heading). */
  readonly as?: ElementType;
}

export const SectionTitle = forwardRef<HTMLElement, SectionTitleProps>(
  ({ as, className, children, ...rest }, ref) => {
    const Component = (as ?? "div") as ElementType;
    return (
      <Component
        ref={ref}
        data-component="section-title"
        className={cx("nai-section-title", className)}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);
SectionTitle.displayName = "SectionTitle";
